import {EnvironmentInjector, inject, Injectable, runInInjectionContext} from "@angular/core";
import {RouterStateSnapshot, TitleStrategy} from "@angular/router";
import {getAllRouterStateData, getSnapshotDeepestChildRoute} from "./utils";
import {Meta, Title} from "@angular/platform-browser";
import {environment} from "../../environments/environment";
import {defaultDescription, defaultTitle} from "./app.routes";
import {take} from "rxjs";
import {DOCUMENT} from "@angular/common";

@Injectable({providedIn: 'root'})
export class TitleAndDescriptionStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly doc = inject(DOCUMENT);

  override updateTitle(snapshot: RouterStateSnapshot): void {

    const title = this.buildTitle(snapshot) ?? defaultTitle;
    this.title.setTitle(title);

    const allRouterStateData = getAllRouterStateData(snapshot);

    const canonicalHref =  runInInjectionContext(this.environmentInjector, () => allRouterStateData.canonicalUrl ? allRouterStateData.canonicalUrl(getSnapshotDeepestChildRoute(snapshot.root).paramMap) : snapshot.url.split('?')[0]);

    if(typeof canonicalHref === 'string') {
      this.setCanonicalUrl(canonicalHref);
    } else {
      canonicalHref.pipe(
        take(1)
      ).subscribe(
        href => this.setCanonicalUrl(href)
      )
    }

    const metaDescriptionField = allRouterStateData.metaDescription;

    let metaDescription;

    if (typeof metaDescriptionField === 'function') {
      metaDescription = runInInjectionContext(this.environmentInjector, () => metaDescriptionField(getSnapshotDeepestChildRoute(snapshot.root)));
    } else {
      metaDescription = metaDescriptionField ?? defaultDescription;
    }

    if (typeof metaDescription === 'string') {
      this.meta.updateTag({name: 'description', content: metaDescription});
    } else {
      metaDescription.pipe(
        take(1)
      ).subscribe(
        description => {
          this.meta.updateTag({name: 'description', content: description});
        }
      )
    }

    const structuredDataFunc = allRouterStateData.structuredData;
    if (structuredDataFunc) {
      const structuredData = runInInjectionContext(this.environmentInjector, () => structuredDataFunc(getSnapshotDeepestChildRoute(snapshot.root).paramMap))

      if (structuredData) {
        structuredData.pipe(
          take(1)
        ).subscribe(
          data => {
            this.upsertRouteJsonLd(data);
          }
        )
      }
    } else {
      this.removeRouteJsonLd();
    }

    const robotsTag = (allRouterStateData.index && environment.indexingEnabled) ? 'index, follow' : 'noindex, nofollow';
    this.meta.updateTag({name: 'robots', content: robotsTag});
  }

  private setCanonicalUrl(canonicalHref: string) {
    const existingCanonical = this.doc.querySelector("link[rel='canonical']");
    if (existingCanonical) {
      existingCanonical.setAttribute('href', `${environment.baseUrl}${canonicalHref}`);
    }
  }

  private upsertRouteJsonLd(payload: object) {
    const ROUTE_JSON_LD_ID = 'route-json-ld';
    let el = this.doc.head.querySelector<HTMLScriptElement>(
      `#${ROUTE_JSON_LD_ID}`
    );
    if (!el) {
      el = this.doc.createElement('script');
      el.type = 'application/ld+json';
      el.id   = ROUTE_JSON_LD_ID;
      this.doc.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
  }

  private removeRouteJsonLd() {
    const el = this.doc.head.querySelector("#route-json-ld");
    if (el) {
      this.doc.head.removeChild(el);
    }
  }
}
